import _ from 'lodash';
import { useState, useMemo, forwardRef, useImperativeHandle, useCallback, useEffect } from 'react';
import { CustomDataTable } from 'components/DatatableComponent';
import {
  GridSortModel,
  getGridStringOperators,
  GridFilterModel,
  GridActionsCellItem,
  GridColumns,
  GridRowOrderChangeParams,
} from '@mui/x-data-grid-pro';
import EditIcon from '@mui/icons-material/Edit';
import { PaginationAndSortingParams } from 'types/api/Common/PaginationTypes';
import { InsightViewAttributeDefinitionPanel } from './InsightViewAttributeDefinitionPanel';
import {
  InsightViewAttributeItem,
  InsightViewVarianceDisplayType,
  InsightViewVarianceGroupDisplayType,
  InsightViewVarianceItem,
} from 'types/api/SystemTenant/AROView/InsightViewDefinitionTypes';
import { buildVarianceColumnOptions, ModalAddVariance } from './ModalAddVariance';

interface IProps {
  disabled?: boolean;
  initialData?: InsightViewVarianceItem[];
  attributeDefinitionData: InsightViewAttributeItem[];
  dataSetType?: string;
}

export interface IInsightViewVarianceDefinitionRef {
  getRowData: () => InsightViewVarianceItem[];
  setRowData: (data: InsightViewVarianceItem[]) => void;
}

export const normalizeVarianceOrder = (data: InsightViewVarianceItem[]): InsightViewVarianceItem[] => {
  return data
    .map((item, originalIndex) => ({
      item,
      originalIndex,
      effectiveOrder: item.order ?? originalIndex,
    }))
    .sort(
      (first, second) =>
        first.effectiveOrder - second.effectiveOrder || first.originalIndex - second.originalIndex
    )
    .map(({ item }, order) => ({ ...item, order }));
};

export const moveVarianceRow = (
  data: InsightViewVarianceItem[],
  oldIndex: number,
  targetIndex: number
): InsightViewVarianceItem[] => {
  if (oldIndex === targetIndex) return normalizeVarianceOrder(data);

  const reordered = [...data];
  const [movedRow] = reordered.splice(oldIndex, 1);
  reordered.splice(targetIndex, 0, movedRow);
  return reordered.map((item, order) => ({ ...item, order }));
};

export const InsightViewVarianceDefinitionTable = forwardRef<any, IProps>((props, ref) => {
  const { disabled: disabledProps, initialData, attributeDefinitionData, dataSetType } = props;
  const disabled = disabledProps ?? attributeDefinitionData.length === 0;

  const [rowData, setRowData] = useState<InsightViewVarianceItem[]>([] as InsightViewVarianceItem[]);
  const [showModalAddVariance, setShowModalAddVariance] = useState<{
    visible: boolean;
    data?: InsightViewVarianceItem;
  }>({
    visible: false,
  });

  const [filter, setFilter] = useState<PaginationAndSortingParams>({
    page: 0,
    size: 100,
    filterValue: '',
    sortType: '',
    filterOperator: '',
  });

  const handleEditClick = (row: InsightViewVarianceItem) => () => {
    if (!row) return;
    setShowModalAddVariance({ visible: true, data: row });
  };

  const varianceColumnLabels = useMemo(
    () =>
      new Map(
        buildVarianceColumnOptions(attributeDefinitionData).map((option) => [option.value.toString(), option.label])
      ),
    [attributeDefinitionData]
  );

  const columnsAttribute = useMemo<GridColumns>(() => {
    return [
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Edit',
        width: 30,
        cellClassName: 'actions',

        getActions: (params) => {
          return [
            // @ts-ignore
            <GridActionsCellItem
              icon={<EditIcon />}
              key={params.id}
              label='Edit'
              onClick={handleEditClick(params.row)}
            />,
          ];
        },
      },
      {
        field: 'group',
        headerName: 'Display Group',
        width: 240,
        editable: false,
        filterable: true,
        sortable: true,
        valueGetter: (params) => {
          if (params?.row?.groupDisplayType === InsightViewVarianceGroupDisplayType.OWN_GROUP) {
            return `${params?.row?.headerName?.trim() || 'Variance'} (Own Group)`;
          }
          return params?.row?.group?.label;
        },
        filterOperators: getGridStringOperators().filter((operator) => ['contains', 'equals'].includes(operator.value)),
      },
      {
        field: 'order',
        headerName: 'Order',
        width: 80,
        editable: false,
        filterable: false,
        sortable: false,
        valueGetter: (params) => (params?.row?.order ?? 0) + 1,
      },
      {
        field: 'varianceDisplayType',
        headerName: 'Displayed Columns',
        width: 160,
        editable: false,
        filterable: true,
        sortable: true,
        valueGetter: (params) => {
          if (params?.row?.weight != null) {
            return 'Weighted Var only';
          }
          switch (params?.row?.varianceDisplayType) {
            case InsightViewVarianceDisplayType.VAR_ONLY:
              return 'Var only';
            case InsightViewVarianceDisplayType.VAR_PERCENTAGE_ONLY:
              return 'Var % only';
            default:
              return 'Var and Var %';
          }
        },
        filterOperators: getGridStringOperators().filter((operator) => ['contains', 'equals'].includes(operator.value)),
      },
      {
        field: 'displayName',
        headerName: 'Display Name',
        width: 160,
        editable: false,
        filterable: true,
        sortable: true,
        valueGetter: (params) => params?.row?.displayName?.trim() || 'Var',
        filterOperators: getGridStringOperators().filter((operator) => ['contains', 'equals'].includes(operator.value)),
      },
      {
        field: 'weight',
        headerName: 'Weight',
        width: 110,
        editable: false,
        filterable: true,
        sortable: true,
        valueGetter: (params) => params?.row?.weight ?? '',
      },
      {
        field: 'resultColumn',
        headerName: 'Result Column',
        width: 240,
        editable: false,
        filterable: true,
        sortable: true,
        valueGetter: (params) => {
          return (
            varianceColumnLabels.get(params?.row?.resultColumn?.value?.toString()) ?? params?.row?.resultColumn?.label
          );
        },
        filterOperators: getGridStringOperators().filter((operator) => ['contains', 'equals'].includes(operator.value)),
      },
      {
        field: 'targetColumn',
        headerName: 'Target Column',
        width: 240,
        editable: false,
        filterable: true,
        sortable: true,
        valueGetter: (params) => {
          return (
            varianceColumnLabels.get(params?.row?.targetColumn?.value?.toString()) ?? params?.row?.targetColumn?.label
          );
        },
        filterOperators: getGridStringOperators().filter((operator) => ['contains', 'equals'].includes(operator.value)),
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [varianceColumnLabels]);

  const handleOnSortModelChange = (model: GridSortModel) => {
    if (model.length === 0) {
      setFilter({ ...filter, sortType: 'asc' });
      return;
    }
    setFilter({ ...filter, sortType: model[0].sort });
  };

  const handleOnFilterModelChange = (params: GridFilterModel) => {
    let filterObj = {};
    if (params.items.length) {
      filterObj = {
        filterValue: params.items[0].value,
        filterOperator: params.items[0].operatorValue,
      };
    } else {
      filterObj = {
        filterValue: '',
        filterOperator: 'contains',
      };
    }
    setFilter({
      ...filter,
      ...filterObj,
    });
  };

  const handleChangePage = (newPage: number) => {
    setFilter({ ...filter, page: newPage });
  };

  const handleChangePageSize = (newPageSize: number) => {
    setFilter({ ...filter, size: newPageSize });
  };

  const handleOnAddVarianceButtonClick = () => {
    setShowModalAddVariance({ visible: true });
  };

  const handleOnCloseModalAddVariance = () => {
    setShowModalAddVariance({ visible: false });
  };

  const handleOnSaveVarianceItem = (data: InsightViewVarianceItem) => {
    setRowData((prevData) => {
      const itemIndex = _.findIndex(prevData, { id: data.id });
      if (itemIndex !== -1) {
        const newData = [...prevData];
        newData[itemIndex] = {
          ...data,
          order: prevData[itemIndex].order ?? itemIndex,
        };
        return normalizeVarianceOrder(newData);
      }

      return [...prevData, { ...data, order: prevData.length }];
    });
  };

  const handleOnDeleteVariance = (data: InsightViewVarianceItem) => {
    setRowData((prevData) => {
      return normalizeVarianceOrder(prevData.filter((item) => item.id !== data.id));
    });
  };

  const handleRowOrderChange = (params: GridRowOrderChangeParams) => {
    setRowData((prevData) => moveVarianceRow(prevData, params.oldIndex, params.targetIndex));
  };

  useImperativeHandle(ref, () => ({
    getRowData: () => rowData,
    setRowData: (data: InsightViewVarianceItem[]) => {
      setRowData(normalizeVarianceOrder(data));
    },
  }));

  const getRowId = useCallback((row) => {
    return row.id;
  }, []);

  useEffect(() => {
    if (initialData) {
      setRowData(normalizeVarianceOrder(initialData));
    }
  }, [initialData]);

  return (
    <>
      <InsightViewAttributeDefinitionPanel
        buttonAddColumnLabel='Add Variance'
        onAddColumnAttributeButtonClick={handleOnAddVarianceButtonClick}
        disabled={disabled}
      />
      <CustomDataTable
        rows={rowData}
        checkboxSelection={false}
        columns={columnsAttribute}
        getRowId={getRowId}
        disableSelectionOnClick
        sortingMode='client'
        filterMode='client'
        paginationMode='client'
        isRowSelectable={(row) => {
          return !row.row.locked;
        }}
        isCellEditable={(params) => {
          return !params?.row?.locked;
        }}
        onSortModelChange={handleOnSortModelChange}
        onFilterModelChange={handleOnFilterModelChange}
        onPageChange={handleChangePage}
        onPageSizeChange={handleChangePageSize}
        rowHeight={45}
        autoHeight={rowData.length !== 0 && rowData.length > 5}
        sx={{ opacity: disabled ? 0.5 : 1, pointerEvents: disabled ? 'none' : 'auto', height: '300px' }}
        onProcessRowUpdateError={(params) => console.log({ params })}
        getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd')}
        experimentalFeatures={{ newEditingApi: true }}
        components={{
          Toolbar: null,
          Pagination: null,
        }}
        rowReordering
        onRowOrderChange={handleRowOrderChange}
      />

      <ModalAddVariance
        visible={showModalAddVariance.visible}
        dataSetType={dataSetType}
        onClose={handleOnCloseModalAddVariance}
        onDelete={handleOnDeleteVariance}
        onSave={handleOnSaveVarianceItem}
        attributeData={attributeDefinitionData}
        selectedData={showModalAddVariance.data}
      />
    </>
  );
});
